import Settings from '../../../../../settings/Settings';
import { $playerProfile, $aliceUpgradeSpecs, $bobUpgradeSpecs } from '../../../../../backend/store/playerProfile';

const WIDTH = 300;
const HEIGHT = 100;

const FRAMES = {
    ON: 'gui/footer/button_on',
    OFF: 'gui/footer/button_off',
};

const PRICE = {
    FONT: Settings.FONTS.LEAGUE_SPARTAN,
    SIZE: 40,
    TINT: 0xffffff,
    SHADOW: { X: 2, Y: 2, TINT: 0x000000, ALPHA: 0.5 },
};

const TITLE = {
    FONT: Settings.FONTS.LXGW_CYRILLIC_BOLD,
    SIZE: 22,
    TINT: 0xffffff,
    SHADOW: { X: 2, Y: 2, TINT: 0x000000, ALPHA: 0.5 },
};

const UPGRADE = {
    FONT: Settings.FONTS.LEAGUE_SPARTAN,
    SIZE: 30,
    TINT: {
        FROM: 0xffffff,
        TO: 0xfbed43,
        MAXED_OUT: 0xf1c40f,
    },
    SHADOW: { X: 2, Y: 2, TINT: 0x000000, ALPHA: 0.5 },
};

const PUSH = {
    DURATION: 80,
    EASE: Phaser.Math.Easing.Linear,
    DIST: 5,
};

type HeroType = 'alice' | 'bob';

type AliceSpecType = keyof ReturnType<typeof $aliceUpgradeSpecs.getState>;
type BobSpecType = keyof ReturnType<typeof $bobUpgradeSpecs.getState>;

type UpgradeData = {
    value: number;
    canUpgrade: boolean;
    upgradePrice: number;
    upgradedValue: number;
};

export default class Button extends Phaser.GameObjects.Container {
    private bg!: Phaser.GameObjects.Image;
    private icon!: Phaser.GameObjects.Image;
    private price!: Phaser.GameObjects.BitmapText;
    private title!: Phaser.GameObjects.BitmapText;
    private upgrade!: Phaser.GameObjects.BitmapText;

    private tweenPush?: Phaser.Tweens.Tween;
    private upgradePrice: number = 0;
    private baseY: number;

    private hero: HeroType;
    private specType: AliceSpecType | BobSpecType;
    private specUpgradeData!: UpgradeData;

    constructor(scene: Phaser.Scene, x: number, y: number, title: string, specType: any, hero: HeroType) {
        super(scene, x, y);

        this.baseY = y;
        this.hero = hero;
        this.specType = specType;

        this.setupBG();
        this.setupPrice();
        this.setupTitle(title);
        this.setupUpgrade();
        this.setupListeners();

        this.positionUpgrade();
        this.positionPrice();
    }

    static get WIDTH() {
        return WIDTH;
    }

    setupBG() {
        this.bg = this.scene.add.image(160, 50, 'atlas', FRAMES.ON);
        this.bg.setOrigin(0);
        this.add(this.bg);

        this.bg.setInteractive({ useHandCursor: true });
        this.bg.on('pointerdown', () => {
            this.emit('click');
            this.push();
        });
    }

    setupPrice() {
        let x = WIDTH / 2 - 40;
        let y = 25;

        this.icon = this.scene.add.image(x, y, 'atlas', 'gui/footer/price_coin');
        this.icon.setOrigin(0, 0.5);
        this.add(this.icon);

        x += this.icon.displayWidth + 8;
        y = 22;

        this.price = this.scene.add.bitmapText(x, y, PRICE.FONT, '0', PRICE.SIZE);
        this.price.setOrigin(0, 0.5);
        this.price.setTint(PRICE.TINT);
        this.price.setDropShadow(PRICE.SHADOW.X, PRICE.SHADOW.Y, PRICE.SHADOW.TINT, PRICE.SHADOW.ALPHA);
        this.add(this.price);
    }

    setupTitle(title: string) {
        let x = WIDTH / 2;
        let y = 70;

        this.title = this.scene.add.bitmapText(x, y, TITLE.FONT, title, TITLE.SIZE);
        this.title.setOrigin(0, 0.5);
        this.title.setTint(TITLE.TINT);
        this.title.setDropShadow(TITLE.SHADOW.X, TITLE.SHADOW.Y, TITLE.SHADOW.TINT, TITLE.SHADOW.ALPHA);
        this.add(this.title);
    }

    setupUpgrade() {
        let x = WIDTH / 2;
        let y = 70;
        let text = `0 > 0`;

        this.upgrade = this.scene.add.bitmapText(x, y, UPGRADE.FONT, text, UPGRADE.SIZE);
        this.upgrade.setOrigin(0, 0.5);
        this.upgrade.setTint(UPGRADE.TINT.FROM);
        this.upgrade.setDropShadow(UPGRADE.SHADOW.X, UPGRADE.SHADOW.Y, UPGRADE.SHADOW.TINT, UPGRADE.SHADOW.ALPHA);
        this.add(this.upgrade);

        let coloredFrom = this.upgrade.text.indexOf('>') + 1;
        this.upgrade.setCharacterTint(coloredFrom, -1, true, UPGRADE.TINT.TO);
    }

    positionUpgrade(): void {
        const btnWidth = this.bg.displayWidth;
        const w1 = this.title.getTextBounds().local.width;
        const w2 = this.upgrade.getTextBounds().local.width;
        const padding = 10;
        const totalWidth = w1 + w2 + padding;

        this.title.x = (btnWidth - totalWidth) / 2;
        this.upgrade.x = this.title.x + padding + w1;
    }

    positionPrice(): void {
        const btnWidth = this.bg.displayWidth;
        const w1 = this.icon.displayWidth;
        const w2 = this.price.getTextBounds().local.width;
        const padding = 10;
        const totalWidth = w1 + w2 + padding;

        this.icon.x = (btnWidth - totalWidth) / 2;
        this.price.x = this.icon.x + padding + w1;
    }

    setupListeners() {
        $playerProfile.watch((state) => this.updateAvailability(state.player_wallet.currency_soft));
        if (this.hero == 'alice')
            $aliceUpgradeSpecs.watch((data) => {
                // console.log(JSON.stringify(this.specUpgradeData))
                this.specUpgradeData = data[this.specType as AliceSpecType];
                this.update();
            });
        else if (this.hero == 'bob')
            $bobUpgradeSpecs.watch((data) => {
                this.specUpgradeData = data[this.specType as BobSpecType];
                this.update();
            });
    }

    enable() {
        this.bg.setInteractive();
        this.bg.setFrame(FRAMES.ON);
    }

    disable() {
        this.bg.disableInteractive();
        this.bg.setFrame(FRAMES.OFF);
    }

    update() {
        this.positionUpgrade();
        this.positionPrice();

        if (!this.specUpgradeData?.canUpgrade) {
            this.setMaxedOut();
            return;
        }
        this.price.setText(String(this.specUpgradeData.upgradePrice));

        let upgrade;
        if (this.specType == 'critical_chance_level' || this.specType == 'extra_gold_level') {
            upgrade = `${Math.round(this.specUpgradeData.value * 100) / 100}% > ${Math.round(this.specUpgradeData.upgradedValue * 100) / 100
                }%`;
        } else upgrade = `${this.specUpgradeData.value} > ${this.specUpgradeData.upgradedValue}`;
        this.upgrade.setText(upgrade);

        this.upgrade.setCharacterTint(0, -1, true, UPGRADE.TINT.FROM);
        let coloredFrom = this.upgrade.text.indexOf('>') + 1;
        this.upgrade.setCharacterTint(coloredFrom, -1, true, UPGRADE.TINT.TO);

        this.upgradePrice = this.specUpgradeData.upgradePrice;
        if ($playerProfile.getState().player_wallet.currency_soft < this.upgradePrice) this.disable();
    }

    updateAvailability(gold: number) {
        if (gold < this.upgradePrice) {
            this.disable();
        } else {
            this.enable();
        }
    }

    setMaxedOut() {
        this.disable();

        this.icon.setVisible(false);
        this.price.setVisible(false);

        this.upgrade.setText(this.scene.cache.json.get('i18n')['maxed_out']);
        this.upgrade.setCharacterTint(0, -1, true, UPGRADE.TINT.MAXED_OUT);
    }

    push() {
        this.tweenPush?.stop();
        this.y = this.baseY;

        this.tweenPush = this.scene.tweens.add({
            targets: this,
            duration: PUSH.DURATION,
            ease: PUSH.EASE,
            y: `+=${PUSH.DIST}`,
            yoyo: true,
        });
    }

    destroy() {
        super.destroy();
    }
}
